// @ts-nocheck
import { useState } from 'react';
import {
  Badge,
  Box,
  Card,
  Code,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@/components/ui';
import { InfiniteScroll, VirtualList } from '@/components/advanced/data';

// ---------------------------------------------------------------------------
// Inline mock data (self-contained, no network / SDK)
// ---------------------------------------------------------------------------

interface MockMember {
  id: number;
  name: string;
  role: string;
  ministry: string;
}

const ROLES = ['Member', 'Volunteer', 'Leader', 'Deacon', 'Elder'];
const MINISTRIES = ['Worship', 'Youth', 'Outreach', 'Hospitality', 'Media', 'Children'];

function makeMembers(start: number, count: number): MockMember[] {
  return Array.from({ length: count }, (_, i) => {
    const id = start + i;
    return {
      id,
      name: `Member ${id}`,
      role: ROLES[id % ROLES.length],
      ministry: MINISTRIES[id % MINISTRIES.length],
    };
  });
}

// A large, pre-built list to demonstrate windowed rendering.
const VIRTUAL_ITEMS: MockMember[] = makeMembers(1, 1000);

export default function DataExtrasPage() {
  // --- InfiniteScroll demo state ---------------------------------------
  const [items, setItems] = useState<MockMember[]>(() => makeMembers(1, 15));
  const [loading, setLoading] = useState(false);
  const hasMore = items.length < 45;

  const handleLoadMore = () => {
    if (loading || !hasMore) return;
    setLoading(true);
    // Simulate an async page load with a local timer (no backend).
    setTimeout(() => {
      setItems((prev) => [...prev, ...makeMembers(prev.length + 1, 15)]);
      setLoading(false);
    }, 600);
  };

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <Title order={2}>Data: Infinite Scroll &amp; Virtual List</Title>
        <Text c="dimmed" size="sm">
          Performance-focused data components for rendering long lists efficiently.
          InfiniteScroll lazily appends pages as the user nears the bottom, while
          VirtualList only renders the rows currently in view (windowing) so it can
          handle thousands of items without jank.
        </Text>
      </Stack>

      {/* InfiniteScroll ---------------------------------------------------- */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>InfiniteScroll</Title>
            <Text size="sm" c="dimmed">
              Wraps a scrollable list and calls <Code>onLoadMore</Code> when a
              sentinel near the end becomes visible. Shows a loading state while
              fetching and an end state when there is nothing left.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>
              Example ({items.length} loaded{hasMore ? ', scroll for more' : ', all loaded'}):
            </Text>
            <Box
              style={{
                maxHeight: 320,
                overflowY: 'auto',
                border: '1px solid var(--mantine-color-default-border)',
                borderRadius: 'var(--mantine-radius-md)',
                padding: 'var(--mantine-spacing-xs)',
              }}
            >
              <InfiniteScroll
                onLoadMore={handleLoadMore}
                hasMore={hasMore}
                loading={loading}
              >
                <Stack gap="xs">
                  {items.map((m) => (
                    <Card key={m.id} withBorder p="sm" radius="sm">
                      <Group justify="space-between" wrap="nowrap">
                        <Text size="sm" fw={500}>
                          {m.name}
                        </Text>
                        <Group gap="xs">
                          <Badge size="sm" variant="light">
                            {m.role}
                          </Badge>
                          <Badge size="sm" variant="outline" color="gray">
                            {m.ministry}
                          </Badge>
                        </Group>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              </InfiniteScroll>
            </Box>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Code:
            </Text>
            <Code block language="tsx">
{`const [items, setItems] = useState(makeMembers(1, 15));
const [loading, setLoading] = useState(false);
const hasMore = items.length < 45;

const handleLoadMore = () => {
  if (loading || !hasMore) return;
  setLoading(true);
  // fetch the next page, then append
  fetchNextPage().then((next) => {
    setItems((prev) => [...prev, ...next]);
    setLoading(false);
  });
};

<InfiniteScroll
  onLoadMore={handleLoadMore}
  hasMore={hasMore}
  loading={loading}
>
  <Stack gap="xs">
    {items.map((m) => (
      <Card key={m.id} withBorder p="sm">
        {m.name}
      </Card>
    ))}
  </Stack>
</InfiniteScroll>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key Props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • onLoadMore - Called when the sentinel scrolls into view (required)
              <br />
              • hasMore - Whether there are more items to load (required)
              <br />
              • loading - Show the loading indicator while fetching
              <br />
              • loadingComponent / endComponent - Override default states
              <br />
              • threshold / rootMargin - Tune the IntersectionObserver trigger
              <br />
              • direction - 'down' (default) or 'up' for reverse lists
            </Text>
          </Stack>
        </Stack>
      </Card>

      {/* VirtualList ------------------------------------------------------- */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Title order={4}>VirtualList</Title>
            <Text size="sm" c="dimmed">
              Renders only the rows visible in the viewport (plus a small
              overscan), so even very large datasets stay smooth. Supports fixed
              or per-index row heights and an optional <Code>onScrollEnd</Code>{' '}
              callback.
            </Text>
          </Stack>

          <Stack gap="sm">
            <Text size="sm" fw={500}>
              Example ({VIRTUAL_ITEMS.length.toLocaleString()} items, windowed):
            </Text>
            <Box
              style={{
                border: '1px solid var(--mantine-color-default-border)',
                borderRadius: 'var(--mantine-radius-md)',
              }}
            >
              <VirtualList
                items={VIRTUAL_ITEMS}
                height={320}
                itemHeight={56}
                gap={4}
                getItemKey={(m) => m.id}
                renderItem={(m) => (
                  <Card withBorder p="sm" radius="sm" style={{ height: '100%' }}>
                    <Group justify="space-between" wrap="nowrap" h="100%">
                      <Stack gap={0}>
                        <Text size="sm" fw={500}>
                          {m.name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {m.ministry}
                        </Text>
                      </Stack>
                      <Badge size="sm" variant="light">
                        {m.role}
                      </Badge>
                    </Group>
                  </Card>
                )}
              />
            </Box>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Code:
            </Text>
            <Code block language="tsx">
{`const items = makeMembers(1, 1000); // thousands of rows is fine

<VirtualList
  items={items}
  height={320}
  itemHeight={56}
  gap={4}
  getItemKey={(m) => m.id}
  renderItem={(m, index) => (
    <Card withBorder p="sm" style={{ height: '100%' }}>
      {m.name}
    </Card>
  )}
/>`}
            </Code>
          </Stack>

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Key Props:
            </Text>
            <Text size="xs" c="dimmed" component="div">
              • items - Array of data to virtualize (required)
              <br />
              • itemHeight - Fixed number, or (index) =&gt; number (required)
              <br />
              • renderItem - (item, index) =&gt; ReactNode (required)
              <br />
              • height - Viewport height for the scroll container (required)
              <br />
              • overscan - Extra rows rendered above/below the viewport
              <br />
              • gap - Vertical spacing between rows
              <br />
              • onScrollEnd / scrollEndThreshold - Hook for loading more
              <br />
              • getItemKey - Stable key for each row
            </Text>
          </Stack>
        </Stack>
      </Card>

      <Divider />

      {/* When to use which ------------------------------------------------- */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>When to use which</Title>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                InfiniteScroll
              </Text>
              <Text size="xs" c="dimmed">
                Best when data is paginated from an API and you want to load more
                as the user scrolls (feeds, activity logs, search results). It
                keeps every loaded row mounted, so pair it with VirtualList for
                truly enormous lists.
              </Text>
            </Stack>

            <Stack gap="xs">
              <Text size="sm" fw={500}>
                VirtualList
              </Text>
              <Text size="xs" c="dimmed">
                Best when the full dataset is already in memory and could be very
                large (directories, ledgers, big tables). Only visible rows are
                rendered, keeping the DOM small and scrolling smooth.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
